import { useEffect, useMemo } from 'react';

import { useApolloClient } from '@apollo/client/react';

import {
  AvalaraTaxItemCode,
  EstimatedMoversDocument,
  FinalReturnCostSummariesDocument,
  OnboardingCostSummariesDocument,
  PricingSetFragment,
  RateGroupKindEnum,
  SalesTaxDocument,
  StorageBundlesDocument,
} from '@graphql/platform';

import { DIMENSIONAL_PLANS } from '@root/constants/pricing';
import { PlanName } from '@root/resources/types/plan_name';

import { extractPlan } from '@utils/pricing/pricing_set';

/** Rate groups shown on the challenger plan slider / inclusions. */
export const CHALLENGER_RATE_GROUP_KINDS = [
  RateGroupKindEnum.Saver,
  RateGroupKindEnum.Flexer,
  RateGroupKindEnum.Mover,
] as const;

/** Plan IDs for every dimensional slider size in the current pricing set. */
export const challengerInclusionPlanIds = (
  pricingSet?: PricingSetFragment,
): string[] => {
  if (!pricingSet) return [];

  return DIMENSIONAL_PLANS.map((plan) => {
    const extracted = extractPlan(
      RateGroupKindEnum.Saver,
      plan.name,
      pricingSet,
    );
    return extracted?.id;
  }).filter((id): id is string => id != null);
};

export const challengerPlanId = (
  planSize: PlanName | undefined,
  pricingSet?: PricingSetFragment,
): string | undefined => {
  if (!planSize || !pricingSet) return undefined;
  return extractPlan(RateGroupKindEnum.Saver, planSize, pricingSet)?.id;
};

/** Stagger challenger prefetch queries to avoid GraphQL rate limits. */
const PREFETCH_STAGGER_MS = 80;

/**
 * Prefetch lightweight per-plan pricing data once ZIP + pricing set resolve.
 * Skips heavy appointment quotes and availabilities.
 */
export const useChallengerPlanPricingPrefetch = (
  zip: string | undefined,
  pricingSet?: PricingSetFragment,
) => {
  const client = useApolloClient();
  const pricingSetId = pricingSet?.id;
  const planIds = useMemo(
    () => challengerInclusionPlanIds(pricingSet),
    [pricingSet],
  );

  useEffect(() => {
    if (!zip || !pricingSetId || planIds.length === 0) return;

    let cancelled = false;
    let staggerIndex = 0;
    const schedule = (run: () => void | Promise<unknown>) => {
      const delayMs = staggerIndex * PREFETCH_STAGGER_MS;
      staggerIndex += 1;
      window.setTimeout(() => {
        if (cancelled) return;
        void Promise.resolve(run()).catch(() => undefined);
      }, delayMs);
    };

    DIMENSIONAL_PLANS.forEach((plan) => {
      schedule(() =>
        client.query({
          query: StorageBundlesDocument,
          variables: {
            cuft: plan.cuft,
            amountCuft: plan.cuft,
            zip,
          },
        }),
      );
    });

    planIds.forEach((planId) => {
      schedule(() =>
        client.query({
          query: EstimatedMoversDocument,
          variables: {
            planId,
            postalCode: zip,
          },
        }),
      );
    });

    schedule(() =>
      client.query({
        query: OnboardingCostSummariesDocument,
        variables: {
          pricingSetId,
          planIds,
          rateGroupKinds: [...CHALLENGER_RATE_GROUP_KINDS],
        },
      }),
    );

    schedule(() =>
      client.query({
        query: FinalReturnCostSummariesDocument,
        variables: {
          pricingSetId,
          planIds,
          rateGroupKinds: [...CHALLENGER_RATE_GROUP_KINDS],
        },
      }),
    );

    schedule(() =>
      client.query({
        query: SalesTaxDocument,
        variables: {
          zipCode: zip,
          avalaraItemCode: AvalaraTaxItemCode.Storage,
        },
        fetchPolicy: 'cache-first',
      }),
    );

    schedule(() =>
      client.query({
        query: SalesTaxDocument,
        variables: {
          zipCode: zip,
          avalaraItemCode: AvalaraTaxItemCode.Default,
        },
        fetchPolicy: 'cache-first',
      }),
    );

    return () => {
      cancelled = true;
    };
  }, [client, planIds, pricingSetId, zip]);
};
